import { toMs } from "./dates";

type DateInput = string | number | Date | null | undefined;

// Eg. "31.12.2025"
export function toDateString(date: Date): string {
  return date.toLocaleDateString(navigator.languages, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

// Eg. "31. Dez. 2025"
export function toMediumDateString(date: Date): string {
  return date.toLocaleDateString(navigator.languages, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Eg. "31. Dez."
function toShortDateString(date: Date): string {
  return date.toLocaleDateString(navigator.languages, { month: "short", day: "numeric" });
}

// Eg. "Mittwoch, 31. Dez."
export function toWeekdayDateString(date: Date): string {
  return date.toLocaleDateString(navigator.languages, {
    month: "short",
    day: "numeric",
    weekday: "long",
  });
}

// Eg. "31.12.2025, 14:15:00"
export function toUtcDateTimeString(date: Date): string {
  return date.toLocaleString(navigator.languages, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
  });
}

// Eg. "now", "5m", "2h", "3d", "31. Dez."
export function compactRelativeTime(date: DateInput): string {
  const ms = toMs(date);
  if (ms === null) return "";
  const { min, hours, days } = unitsSince(ms);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return toShortDateString(new Date(ms));
}

// Eg. "just now", "5 min ago", "2 hours ago", "3 days ago", "31. Dez. 2025"
export function relativeTimeAgo(date: DateInput): string {
  const ms = toMs(date);
  if (ms === null) return "";
  const { min, hours, days } = unitsSince(ms);
  if (min < 1) return "just now";
  if (min === 1) return "1 min ago";
  if (min < 60) return `${min} min ago`;
  if (hours === 1) return "1 hour ago";
  if (hours < 24) return `${hours} hours ago`;
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  return toMediumDateString(new Date(ms));
}

function unitsSince(target: number) {
  const diff = Math.max(0, Date.now() - target);
  const min = Math.floor(diff / 60000);
  const hours = Math.floor(min / 60);
  const days = Math.floor(hours / 24);
  return { min, hours, days };
}

// use only for <input type="date">
export function toInputDateString(date: Date | string | null | undefined): string | undefined {
  return date instanceof Date ? date.toISOString().slice(0, 10) : undefined;
}

// use only for <input type="datetime">
export function toInputDateTimeString(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
